import type { EpisodeRef, OutputKind, OutputLine } from "@dramapie/shared";

import { ApiError } from "./api";

/**
 * M8 产出线的**前端纯逻辑**：排序 / 可见性 / 默认线 / 集归属计数 / 409 文案。
 *
 * 服务端已经按 order 排好了，但前端不能依赖「响应顺序 = 展示顺序」：
 * 用户 PATCH order 之后本地缓存可能还没来得及刷，这里统一再排一次。
 */

export const OUTPUT_KIND_LABEL: Record<OutputKind, string> = {
  drama: "短剧",
  film: "微电影",
  comic: "条漫",
  anim: "动画版",
  mv: "MV",
};

export const OUTPUT_KIND_OPTIONS: ReadonlyArray<{ value: OutputKind; label: string }> = (
  Object.keys(OUTPUT_KIND_LABEL) as OutputKind[]
).map((value) => ({ value, label: OUTPUT_KIND_LABEL[value] }));

/** 契约 §1 冻结：产出线画幅只有竖屏 / 横屏。 */
export const OUTPUT_ASPECT_OPTIONS = ["9:16", "16:9"] as const;

/** order 升序，同 order 按 id（与服务端 `sortOutputLines` 同口径）。 */
export function sortOutputs(items: readonly OutputLine[]): OutputLine[] {
  return [...items].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** 切换器主列表：隐藏与归档都不出现（管理浮层里仍然列全量）。 */
export function visibleOutputs(items: readonly OutputLine[]): OutputLine[] {
  return sortOutputs(items).filter((item) => !item.hidden && !item.archived);
}

/** 归档 = 只读；隐藏只是不打扰。两者都不许往里加集 / 生成。 */
export function isOutputReadOnly(output: OutputLine | null | undefined): boolean {
  return Boolean(output?.archived);
}

export function findOutput(
  items: readonly OutputLine[],
  outputId: string | null | undefined,
): OutputLine | null {
  if (!outputId) return null;
  return items.find((item) => item.id === outputId) ?? null;
}

/**
 * 默认产出线：第一条**未归档**的（隐藏可以当默认，归档不行）。
 * 全归档 / 全隐藏时退到第一条；空列表返回 null（由调用方显示空状态）。
 */
export function defaultOutputId(items: readonly OutputLine[]): string | null {
  const sorted = sortOutputs(items);
  return (sorted.find((item) => !item.archived) ?? sorted[0])?.id ?? null;
}

/**
 * 当前产出线：`?out=` 命中就用它；没命中（被删了 / 手写错 / 老链接）回落到默认线。
 * 这样深链不会因为一条产出线被删就整页空白。
 */
export function resolveCurrentOutput(
  items: readonly OutputLine[],
  outputId: string | null | undefined,
): OutputLine | null {
  return findOutput(items, outputId) ?? findOutput(items, defaultOutputId(items));
}

/** 每行「N 集」：按 `episodes[].outputId` 统计。 */
export function countEpisodesByOutput(
  episodes: readonly EpisodeRef[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const episode of episodes) {
    counts[episode.outputId] = (counts[episode.outputId] ?? 0) + 1;
  }
  return counts;
}

/** 左栏集列表：只显示当前产出线的集（契约 §3「切换产出线 = 切换集列表」）。 */
export function episodesOfOutput(
  episodes: readonly EpisodeRef[],
  outputId: string | null | undefined,
): EpisodeRef[] {
  if (!outputId) return [];
  return episodes.filter((episode) => episode.outputId === outputId);
}

/**
 * 删除受阻的文案（契约 §4 验收 4）：409 `output_in_use` 要说明「还有 N 集」，
 * 而不是把服务端的原始 message 直接丢给用户。
 */
export function outputDeleteBlockedMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === "output_in_use") {
    const episodes = Number(error.details?.episodes);
    if (Number.isFinite(episodes) && episodes > 0) {
      return `该产出线还有 ${episodes} 集，先把它们移到别的产出线或删掉`;
    }
    return "该产出线还有集挂靠，先把它们移走或删掉";
  }
  if (error.status === 422) return "至少保留一条产出线，最后一条不能删";
  return null;
}

/** 产出线的一行摘要（`9:16 · 3 集 · 短剧`），列表与触发按钮共用。 */
export function outputMetaLabel(output: OutputLine, episodeCount: number): string {
  return `${output.aspect} · ${episodeCount} 集 · ${OUTPUT_KIND_LABEL[output.kind]}`;
}
